import { CompanyTiers, CompanyStatuses, ClinicStatuses, ContractStatuses, FileTypes, ActivityTypes, ActivityOutcomes } from '../../types/crm';

const generateCompanies = (org) => [
  {
    id: 'company-1',
    name: 'Advanced Dermatology Group',
    city: 'Atlanta',
    state: 'GA',
    industry: 'Dermatology',
    tier: CompanyTiers.ENTERPRISE,
    status: CompanyStatuses.ACTIVE,
    repId: 'rep-1',
    repName: 'Sarah Johnson',
    clinics: 8,
    users: 24,
    revenue12m: org === 'bare' ? 125000 : 180000,
    orders12m: 48,
    aov12m: org === 'bare' ? 2600 : 3750,
    lastOrder: '2024-01-12'
  },
  {
    id: 'company-2', 
    name: 'Premier Medical Center',
    city: 'Denver',
    state: 'CO',
    industry: 'Multi-Specialty',
    tier: CompanyTiers.PREMIUM,
    status: CompanyStatuses.ACTIVE,
    repId: 'rep-2',
    repName: 'Mike Chen',
    clinics: 5,
    users: 15,
    revenue12m: org === 'bare' ? 85000 : 120000,
    orders12m: 32,
    aov12m: org === 'bare' ? 2650 : 3750,
    lastOrder: '2024-01-08'
  },
  {
    id: 'company-3',
    name: 'Metro Health Systems', 
    city: 'Phoenix',
    state: 'AZ',
    industry: 'Hospital System',
    tier: CompanyTiers.ENTERPRISE,
    status: CompanyStatuses.AT_RISK,
    repId: 'rep-3',
    repName: 'Lisa Rodriguez',
    clinics: 12,
    users: 45,
    revenue12m: org === 'bare' ? 220000 : 380000,
    orders12m: 72,
    aov12m: org === 'bare' ? 3050 : 5280,
    lastOrder: '2023-12-15'
  }
];

const generateClinics = (companyId, org) => {
  const clinicNames = [
    'Main Campus', 'North Branch', 'Downtown Office', 'Westside Clinic', 
    'East Location', 'Southpoint Center', 'Medical Plaza', 'Outpatient Center'
  ];
  
  const cities = ['Atlanta', 'Marietta', 'Alpharetta', 'Decatur', 'Sandy Springs'];
  
  return Array.from({ length: Math.floor(3 + Math.random() * 6) }, (_, i) => ({
    id: `clinic-${companyId}-${i + 1}`,
    name: clinicNames[i % clinicNames.length],
    city: cities[i % cities.length],
    state: 'GA',
    units30d: Math.floor(10 + Math.random() * 40),
    units90d: Math.floor(35 + Math.random() * 120),
    lastOrder: new Date(Date.now() - Math.random() * 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    status: i === 0 ? ClinicStatuses.ACTIVE : (i % 3 === 0 ? ClinicStatuses.DORMANT : ClinicStatuses.ACTIVE),
    contacts: Math.floor(1 + Math.random() * 8)
  }));
};

const generateContracts = (companyId) => [
  {
    id: `contract-${companyId}-1`,
    start: '2023-01-01',
    end: '2024-12-31', 
    terms: 'Enterprise Tier - Volume Discount 15%',
    status: ContractStatuses.ACTIVE
  },
  {
    id: `contract-${companyId}-2`,
    start: '2024-01-01',
    end: '2024-03-31',
    terms: 'Trial Agreement - 90 days',
    status: ContractStatuses.EXPIRING
  }
];

const generateFiles = (companyId) => [
  {
    id: `file-${companyId}-1`,
    name: 'Master Service Agreement.pdf',
    uploadedAt: '2023-12-15T10:30:00Z',
    sizeKb: 245,
    type: FileTypes.PDF
  },
  {
    id: `file-${companyId}-2`, 
    name: 'Quality Certificate Dec 2023.pdf',
    uploadedAt: '2023-12-20T14:15:00Z',
    sizeKb: 156,
    type: FileTypes.PDF
  },
  {
    id: `file-${companyId}-3`,
    name: 'Clinic Photo - Main Campus.jpg', 
    uploadedAt: '2024-01-05T09:20:00Z',
    sizeKb: 1240,
    type: FileTypes.IMAGE
  }
];

const generateCompanyActivities = (companyId, org) => {
  const activities = Object.values(ActivityTypes);
  const outcomes = Object.values(ActivityOutcomes);
  
  return Array.from({ length: 20 }, (_, i) => ({
    id: `activity-${companyId}-${i + 1}`,
    repId: 'rep-1',
    companyId,
    companyName: 'Advanced Dermatology Group',
    type: activities[i % activities.length],
    outcome: outcomes[i % outcomes.length], 
    ts: new Date(Date.now() - Math.random() * 60 * 24 * 60 * 60 * 1000).toISOString(),
    note: `Discussed ${org === 'bare' ? 'Lipoderma' : 'Equicent'} ${activities[i % activities.length].toLowerCase()} implementation and next steps`
  }));
};

// Service functions
export const getCompany = async (org, companyId) => {
  const companies = generateCompanies(org);
  return companies.find(c => c.id === companyId) || companies[0];
};

export const getCompanies = async (org, filters = {}) => {
  return generateCompanies(org);
};

export const getCompanyClinics = async (org, companyId, filters = {}) => {
  return generateClinics(companyId, org);
};

export const getCompanyContracts = async (org, companyId) => {
  return generateContracts(companyId);
};

export const getCompanyActivities = async (org, companyId, filters = {}) => {
  return generateCompanyActivities(companyId, org);
};

export const getCompanyFiles = async (org, companyId) => {
  return generateFiles(companyId);
};

export const assignCompanyRep = async (org, companyId, repId) => {
  // Mock implementation - in real app would update database
  console.log(`Assigned rep ${repId} to company ${companyId}`);
  return Promise.resolve();
};

export const addClinic = async (org, companyId, payload) => {
  const newClinic = {
    id: `clinic-${companyId}-${Date.now()}`,
    name: payload.name,
    city: payload.city,
    state: payload.state,
    units30d: 0,
    units90d: 0,
    lastOrder: null,
    status: ClinicStatuses.NEW,
    contacts: 0
  };
  
  return newClinic;
};

export const uploadCompanyFile = async (org, companyId, file) => {
  const newFile = {
    id: `file-${companyId}-${Date.now()}`,
    name: file.name,
    uploadedAt: new Date().toISOString(),
    sizeKb: Math.round(file.size / 1024),
    type: file.type.includes('pdf') ? FileTypes.PDF : 
           file.type.includes('image') ? FileTypes.IMAGE : FileTypes.DOC
  };
  
  return newFile;
};
